import { useEffect } from "react";

interface Address {
  addressLine1?: string;
  addressLine2?: string;
  emirates?: string;
  country?: string;
  community?: string;
  longitude?: string | number;
  latitude?: string | number;
}

interface PropertyDetails {
  makaniNumber?: string;
  landNnumber?: string;
  DMNumber?: string;
  dewaPremiseNumber?: string;
  alternatePropertyName?: string;
  buildingName?: string;
  propertyReferenceNumber?: string;
  businessUnitName?: string;
  propertyCode?: string;
  erpLaName?: string;
  propertyAdmin?: string;
  ownerData?: any;
  lessorData?: any;
  beneficiaryData?: any;
  address?: Address;
  ownerNumber?: string;
}

export const usePopulatePropertyDetails = ({
  propertyDetails,
  bankList,
  setValue,
  setOwnerInitail,
  setBankNameInitial,
  removeCountryCode,
}: {
  propertyDetails: PropertyDetails;
  bankList?: any[];
  setValue?: Function;
  setOwnerInitail?: any;
  setBankNameInitial?: Function;
  removeCountryCode?: (val: string) => string;
}) => {
  useEffect(() => {
    if (!propertyDetails || Object.keys(propertyDetails).length === 0) return;

    // 🧱 Basic property details
    setValue("makaniNumber", propertyDetails?.makaniNumber);
    setValue("landNnumber", propertyDetails?.landNnumber);
    setValue("DMNumber", propertyDetails?.DMNumber || propertyDetails?.dewaPremiseNumber);
    setValue("alternatePropertyName", propertyDetails?.alternatePropertyName);
    setValue("buildingName", propertyDetails?.buildingName);
    setValue("propertyReferenceNumber", propertyDetails?.propertyReferenceNumber);
    setValue("businessUnitName", propertyDetails?.businessUnitName);
    setValue("propertyCode", propertyDetails?.propertyCode);

    // 🧍‍♂️ Owner setup
    setOwnerInitail({
      value: propertyDetails?.ownerData?.name,
      label: propertyDetails?.ownerData?.name,
      ownerNumber: propertyDetails?.ownerData?.ownerNumber,
      ownerNationality: propertyDetails?.ownerData?.nationality,
      ownerId: propertyDetails?.ownerData?.ownerId,
    });

    if (propertyDetails?.erpLaName) {
      setValue("propertyAdmin", {
        value: propertyDetails?.propertyAdmin,
        label: propertyDetails?.erpLaName,
      });
    }

    if (propertyDetails?.ownerData?.nationality) {
      setValue("ownerNationality", {
        value: propertyDetails?.ownerData?.nationality,
        label: propertyDetails?.ownerData?.nationality,
      });
    }

    // 🏠 Address setup
    const address = propertyDetails?.address;
    if (address) {
      setValue("addressLine1", address?.addressLine1);
      setValue("addressLine2", address?.addressLine2);

      if (address?.emirates) {
        setValue("emirates", { value: address.emirates, label: address.emirates });
      }

      if (address?.country) {
        setValue("country", { value: address.country, label: address.country });
      }

      if (address?.community) {
        setValue("community", { value: address.community, label: address.community });
      }

      if (address?.longitude) setValue("longitude", Number(address.longitude));
      if (address?.latitude) setValue("latitude", Number(address.latitude));
    }

    // 🧾 Lessor & Beneficiary
    setValue("ownerNumber", propertyDetails?.ownerData?.number || propertyDetails?.ownerNumber);

    const lessor = propertyDetails?.lessorData;
    if (lessor) {
      if (lessor.lessorLetterFax) setValue("lessorFax", lessor.lessorLetterFax);
      if (lessor.lessorNameAr) setValue("lessorNameAr", lessor.lessorNameAr);
      if (lessor.lessorEmail) setValue("lessorEmail", lessor.lessorEmail);
      if (lessor.lessorLetterPOBox) setValue("lessorPOBox", lessor.lessorLetterPOBox);
      if (lessor.lessorTel && removeCountryCode) {
        setValue("lessorPhone", removeCountryCode(lessor.lessorTel), { shouldValidate: true });
      }
      if (lessor.lessorLicenseNo) setValue("lessorLicenseNo", lessor.lessorLicenseNo);
      if (lessor.lessorAddress) setValue("lessorAddress", lessor.lessorAddress);
    }

    const beneficiary = propertyDetails?.beneficiaryData;
    if (beneficiary && bankList?.length > 0) {
      const selectedBank = bankList.find((bank) => bank.label === beneficiary.beneficiaryBank);
      if (selectedBank) setBankNameInitial(selectedBank);
      if (beneficiary.beneficiaryAccountNumber) setValue("accountNo", beneficiary.beneficiaryAccountNumber);
      if (beneficiary.beneficiaryIBAN) setValue("iban", beneficiary.beneficiaryIBAN);
    }
  }, [propertyDetails, bankList]);
};
